/**
 * The provider portal's read surface (EP-004P Phase 1).
 *
 * Every type here mirrors what `emergeDaySheet.js` actually returns. Where a
 * column is nullable in the database it is nullable here, because the screens
 * have to say "not recorded" rather than render an empty cell that reads as a
 * value of zero or an empty string.
 *
 * There is deliberately no write method. Every field a provider would edit —
 * scheduling status, delivery date, visit completion — is MONDAY-owned under
 * EP-004A, and the payload says so in `provenance.writable`. A write method
 * here would be a promise the backend refuses to keep.
 */

import { api } from '../../services/api';
import type { RoundProgress } from './roundProgress';

/** A building's closure for scheduling (dropped / do not schedule), as the closed-building guard defines it. */
export interface BuildingClosure {
  kind: 'DROPPED' | 'DO_NOT_SCHEDULE' | 'MERGED' | null;
  reason: string | null;
  /** Ruling 2026-10-19: closed for providers = a CLOSED scheduling profile, a merge, or business status Dropped. */
  source?: 'SCHEDULING_PROFILE' | 'MERGED' | 'BUSINESS_STATUS';
  /** For a merge: the building it was merged into. */
  into?: string | null;
}

export interface ProviderFacility {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  region: string | null;
  /** Closed for scheduling (verification 2026-10-19): listed, marked and sorted last. Absent from an older server. */
  closure?: BuildingClosure | null;
}

/**
 * `enterprise` means the caller's capabilities are not facility-constrained.
 * They get a bounded, searchable window onto the estate rather than a
 * switcher: `facilities` is the current page, `total` how many exist, and
 * `searchable` says the server will filter on `q`. A facility-constrained
 * caller gets their own buildings and no search.
 */
export interface FacilityScope {
  enterprise: boolean;
  facilities: ProviderFacility[];
  total?: number;
  searchable?: boolean;
  query?: string;
  /**
   * Shared external workbooks the provider works in alongside this portal.
   * Configured per deployment, not per facility; null when not configured,
   * so the shell shows nothing rather than a dead link.
   */
  links?: { stock_report: string | null };
}

export interface DaySheetFacility {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  county: string | null;
  region: string | null;
  phone: string | null;
  corporation: string | null;
  /** The board's Licensed Beds (text). */
  licensed_beds: string | null;
  /** The board's "# of Beds" — a different field; shown under its own name, never merged. */
  beds: number | string | null;
  active_clients: string | null;
  /** Derived: confirmed residents here whose PA lead is ENROLLED. Not the board's hand-typed Active Clients. */
  enrolled_residents: number | null;
  status: string | null;
  credentialing_status: string | null;
}

export interface FacilityContact { id: string; name: string | null; title: string | null; phone: string | null; email: string | null }
export interface CheckInRecord {
  id: string; checked_in_at: string; contact_id: string | null; contact_name_snapshot: string | null; contact_role_snapshot: string | null;
}

/**
 * Testing status — should the provider screen this resident, or does
 * someone need to verify them first? One canonical value derived on the
 * server from the structured qualification field and an approved whitelist
 * of operational words in the tag columns. `conflict` means an eligible and
 * an ineligible word are both on the record (Dagmar): verify, do not guess.
 */
export type TestingStatus =
  | 'QUALIFIED' | 'QUALIFIED_2ND_TIER' | 'NEEDS_VERIFICATION' | 'UNABLE_TO_ACCESS' | 'MEDICAID_PENDING' | 'NEED_SSN'
  | 'NO_MEDICAID' | 'LOST_MEDICAID' | 'WRONG_TYPE_MEDICAID' | 'DNQ' | 'DNQ_PAYER_ISSUE' | 'DNQ_PL_TOO_LOW' | 'PRIVATE_PAY';
export const TESTING_STATUS_LABEL: Record<TestingStatus, string> = {
  QUALIFIED: 'Qualified', QUALIFIED_2ND_TIER: 'Qualified · 2nd tier', NEEDS_VERIFICATION: 'Needs verification',
  UNABLE_TO_ACCESS: 'Unable to access', MEDICAID_PENDING: 'Medicaid pending', NEED_SSN: 'Need SS #',
  NO_MEDICAID: 'No Medicaid', LOST_MEDICAID: 'Lost Medicaid', WRONG_TYPE_MEDICAID: 'Wrong type Medicaid',
  DNQ: 'DNQ', DNQ_PAYER_ISSUE: 'DNQ · payer issue', DNQ_PL_TOO_LOW: 'DNQ · PL too low', PRIVATE_PAY: 'Private pay',
};
/** The hover text for a testing-status badge: where it came from, and whether Monday disagrees. */
export function testingTitle(p: {
  testing_status: TestingStatus | null; testing_status_values: TestingStatus[]; testing_status_conflict: boolean;
  testing_status_source: RosterPatient['testing_status_source']; testing_status_monday: TestingStatus | null; testing_status_drift: boolean;
}): string | undefined {
  const parts: string[] = [];
  if (p.testing_status_conflict) parts.push(`Conflicting on the record: ${p.testing_status_values.map((v) => TESTING_STATUS_LABEL[v]).join(' and ')}`);
  if (p.testing_status_source === 'COUNSELEAR') {
    parts.push('From CounselEAR');
    if (p.testing_status_drift) parts.push(`Monday says ${p.testing_status_monday ? TESTING_STATUS_LABEL[p.testing_status_monday] : 'nothing'}`);
  } else if (p.testing_status_source === 'TAGS') {
    parts.push('Read from the tag column');
  }
  return parts.length ? parts.join(' · ') : undefined;
}

/** Test / verify first / do not test — the three things a provider needs to know at a glance. */
export function testingTone(s: TestingStatus | null): 'ok' | 'attn' | 'stop' | '' {
  if (!s) return '';
  if (s === 'QUALIFIED' || s === 'QUALIFIED_2ND_TIER') return 'ok';
  if (s === 'NEEDS_VERIFICATION' || s === 'MEDICAID_PENDING' || s === 'NEED_SSN') return 'attn';
  return 'stop';
}

export type ResidentStatus = 'Active' | 'Prospect' | 'Inactive' | 'Deceased';
export const RESIDENT_STATUSES: readonly ResidentStatus[] = ['Active', 'Prospect', 'Inactive', 'Deceased'];
export type RosterOrder = 'name' | 'room';

/**
 * The manifest's lenses (April Torino, 2026-09-09): Census · Active ·
 * Enrolled as chips, the rest under More. The server owns every predicate
 * (`daySheetService.ROSTER_LENSES`) and returns every count; this is what
 * the screen calls them and what each means to a provider. Census and
 * Active are the same rows today, by April's own definitions, and stay two
 * lenses so they can diverge without a screen change. ATD is the board's ATD
 * column saying Yes — approved to dispense; Dispensed means a current
 * hearing-aid wearer and is not on the tab (April, 2026-09-10). Enrolled and
 * ATD leave Inactive and Deceased to More, where they stay reachable.
 */
export type RosterLens = 'manifest' | 'census' | 'active' | 'enrolled' | 'atd' | 'inactive' | 'deceased' | 'no-status';
export const DEFAULT_LENS: RosterLens = 'manifest';
export const ROSTER_LENSES: ReadonlyArray<{ key: RosterLens; label: string; primary: boolean; what: string }> = [
  { key: 'manifest', label: 'Current manifest', primary: true, what: 'Who to consider on this visit: the census without residents screened DNQ whose annual is not yet due. New referrals are here.' },
  { key: 'census', label: 'Census', primary: true, what: 'Everyone we presently understand to be at this facility: Active or Prospect.' },
  { key: 'active', label: 'Active', primary: false, what: 'Active or Prospect. Prospect is a new opportunity or new referral.' },
  { key: 'enrolled', label: 'Enrolled', primary: true, what: 'Residents whose Patient Advocate lead is enrolled, not inactive or deceased.' },
  { key: 'atd', label: 'ATD', primary: true, what: 'Approved to dispense: the ATD column says Yes. Residents already dispensed are current hearing-aid wearers and are not here.' },
  { key: 'inactive', label: 'Inactive', primary: false, what: 'Resident status Inactive.' },
  { key: 'deceased', label: 'Deceased', primary: false, what: 'Resident status Deceased.' },
  { key: 'no-status', label: 'No status recorded', primary: false, what: 'A confirmed placement with no resident status on the board.' },
];
export const isLens = (v: string | null | undefined): v is RosterLens => ROSTER_LENSES.some((l) => l.key === v);
export const lensLabel = (k: RosterLens): string => ROSTER_LENSES.find((l) => l.key === k)?.label || k;

/**
 * A list, not an object — one row per facility is expected but nothing in the
 * schema enforces it, and the service returns all of them rather than picking
 * one silently.
 */
export interface SchedulingRow {
  status: string | null;
  date_of_last_visit: string | null;
  provider_next_visit_at: string | null;
  tentative_next_visit: string | null;
  manifest_uploaded: string | null;
  census_date: string | null;
  tm_follow_up_visit_date: string | null;
  comments: string | null;
  /**
   * Link to the facility's SharePoint charting workbook — the document
   * providers actually work in. May be null: not every facility has one
   * recorded, and that absence is itself worth showing rather than hiding.
   */
  cloud_manifest_url: string | null;
}

export interface PendingFit {
  id: string;
  serial_number: string | null;
  ear: string | null;
  hearing_aid_type: string | null;
  status: string | null;
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  room_number: string | null;
}

/**
 * One resident, many devices (April Torino, 2026-09-11). The disposition is
 * derived from the device rows and the board's replacement words — ATD or
 * REPLACEMENT, and which ears — never a tag copied through. Device rows stay
 * underneath; a serial that does not exist yet is "not yet assigned", not
 * missing, because serials are only written after dispense.
 */
export interface FitResident {
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  room_number: string | null;
  disposition: { kind: 'ATD' | 'REPLACEMENT'; ears: Array<'LEFT' | 'RIGHT'> };
  devices: Array<Pick<PendingFit, 'id' | 'serial_number' | 'ear' | 'hearing_aid_type' | 'status'>>;
}
/** A device the board still shows undelivered that an authoritative source records as dispensed. Ids only. */
export interface DispensedElsewhere { id: string; patient_id: string; dispensed_by: Array<'COUNSELEAR' | 'PROVIDER_RECORD'> }

export function dispositionLine(d: FitResident['disposition']): string {
  const ears = d.ears.length === 2 ? 'BOTH' : d.ears[0] || null;
  return [d.kind === 'REPLACEMENT' ? 'ATD — REPLACEMENT' : 'ATD', ears].filter(Boolean).join(' — ');
}

/** Month and year only: the business ball-parks the annual; a day would be invented precision. */
export function monthYear(value: string | null | undefined): string | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})/.exec(String(value));
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export interface BlockedUmed {
  id: string;
  faxed_status: string | null;
  status: string | null;
  submitted_date: string | null;
  last_follow_up_date: string | null;
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  room_number: string | null;
}

export interface AtdSchedule {
  state: 'NOT_PLANNED' | 'ON_MANIFEST' | 'PIT_STOP';
  visit_plan_id: string | null;
  visit_on: string | null;
  pit_stop_on: string | null;
  on_this_visit: boolean;
  /** Why the plan no longer holds at THIS building (VISIT_OTHER_BUILDING arrives with state NOT_PLANNED). */
  attention: 'VISIT_OTHER_BUILDING' | 'VISIT_CANCELLED' | 'VISIT_COMPLETED' | 'VISIT_PASSED' | 'PIT_STOP_PASSED' | null;
}

/**
 * `counselear_id` is absent — not null — for anyone the caller is not care
 * assigned to, which is why it is optional here. `care_assigned` states the
 * reason so a screen never has to infer it from the missing field.
 */
export interface RosterPatient {
  id: string;
  first_name: string | null;
  last_name: string | null;
  /** The room shown: the provider's eMerge room when there is one (20261022a), else Monday's. */
  room_number: string | null;
  /** True when room_number is the provider's eMerge room, not Monday's. Absent on a payload from before the deploy. */
  room_edited?: boolean;
  /** Monday's room (patients.room_number), beside the shown one. Absent before the deploy. */
  room_monday?: string | null;
  /** When the eMerge room was set; null when there is none. Absent before the deploy. */
  room_edited_at?: string | null;
  /**
   * The status shown (20261022d): the eMerge status a provider or the office set (Active or Inactive) when there is
   * one, else Monday's. Monday's Deceased always shows. Null: no status recorded.
   */
  status: string | null;
  /** Monday's status (patients.status), beside the shown one. Absent before the deploy. */
  status_monday?: string | null;
  /** True when `status` is eMerge's, not Monday's. Absent before the deploy. */
  status_edited?: boolean;
  /** Why an eMerge Inactive (DISCHARGED, MOVED, OTHER); null otherwise. */
  status_reason?: StatusReason | null;
  /** When the eMerge status was set; null when there is none. */
  status_edited_at?: string | null;
  /** The short note with an eMerge Inactive. Released only with the care-assigned fields (PHI). */
  status_note?: string | null;
  /** The status box goes on this row (the server's word: a provider's own row, or every row for the office). */
  status_editable?: boolean;
  /** Finance has recorded this resident's death (the report; the status itself is not changed here). */
  death_reported?: boolean;
  /**
   * An open death-report REQUEST a provider filed (ruling 2026-10-22: it waits on Finance): "Death reported, waiting on
   * Finance". `withdrawable` when this caller filed it, or is the office. Null when none is open.
   */
  death_request?: DeathRequest | null;
  phase: string | null;

  /**
   * A text[] in the database, not a scalar — and it genuinely carries more
   * than one value: 26 residents are both MEDICAID PENDING and QUALIFIED.
   * The manifest spreadsheet has a single STATUS cell and cannot express
   * that, so it records whichever the author picked. Rendering the array is
   * how the platform stops losing the second value.
   */
  qualification_status: string[] | null;
  priority: string | null;
  /** The board's own word: Man, Woman, Nonbinary/Other. Not translated. */
  gender: string | null;
  testing_status: TestingStatus | null;
  testing_status_values: TestingStatus[];
  testing_status_conflict: boolean;
  /**
   * COUNSELEAR once the resident's CounselEAR record has been read: the
   * portal is 1:1 with CounselEAR, and Monday's reading is then carried in
   * `testing_status_monday`, with `testing_status_drift` when they differ.
   * The Monday-derived sources remain for residents not yet read.
   */
  testing_status_source: 'COUNSELEAR' | 'QUALIFICATION' | 'TAGS' | 'BOTH' | null;
  testing_status_monday: TestingStatus | null;
  testing_status_drift: boolean;
  testing_status_synced_at: string | null;
  /** The board's annual-screening flag (Annual Appointment Status = Due). */
  annual_due: boolean;
  annual_appt_date: string | null;
  /** Derived: this resident's PA lead is ENROLLED. */
  enrolled: boolean;
  /**
   * Census versus current manifest (April, 2026-09-11). A resident in the
   * census but off the manifest says why; today the one reason is a DNQ
   * screening whose annual is not yet due. New referral is what Prospect
   * means to the business.
   */
  on_manifest?: boolean;
  manifest_exclusion?: 'SCREENED_DNQ' | null;
  /** Put back on the Current manifest (20261027i, "Add back"): the summary fact; who and why are the round's. */
  added_back?: boolean;
  /** Native ATD: where an approved-to-dispense resident stands on the schedule. Present only on the 'atd' lens. */
  atd_schedule?: AtdSchedule;
  new_referral?: boolean;
  /** The board's DNQ date: when the resident was screened and did not qualify. */
  dnq_date?: string | null;

  /**
   * Office-side workflow marks. Released with the census.
   */
  office_tags?: string[] | null;

  /*
   * Everything below is released ONLY to a caller care-assigned to this
   * patient, and is ABSENT — not null — otherwise. `care_assigned` states the
   * reason, so a screen never has to infer it from a missing field.
   *
   * `tags` looks like it belongs with office_tags and does not: the real
   * manifests carry "Screened-NO HT", "Current Hearing Aids" and "Debrox", a
   * medication, in that column.
   */
  counselear_id?: string | null;
  tags?: string[] | null;
  provider_tags?: string[] | null;
  alerts?: string | null;
  notes?: string | null;
  care_assigned: boolean;
  /**
   * Why the clinical fields were released (ruling 2026-10-20): an explicit care
   * assignment, or the caller's facility coverage of the resident's building.
   * Absent on a payload from before the deploy.
   */
  care_basis?: CareBasis;
  /**
   * The release was a PROVIDER's relationship (coverage or a clinical assignment), not a PA / UMED / Finance work
   * assignment (ruling 2026-10-22). Gates the room box. Absent before the deploy, which offers nothing.
   */
  provider_care?: boolean;
  /**
   * The room box is offered on this row (rulings 2026-10-22): a provider who works the resident, a PA holding an
   * assignment to them, or the office. Absent before the deploy, which offers nothing. The PUT decides again.
   */
  room_editable?: boolean;
  /** Why they were withheld, when they were. Absent on a payload from before the deploy. */
  chart_lock?: ChartLock | null;
}

export type CareBasis = 'CARE_ASSIGNMENT' | 'FACILITY_COVERAGE';

/** The server's lock codes (backend authz/coverageCare.js LOCK). */
export type ChartLock =
  | 'NO_COVERAGE_HERE'
  | 'BUILDING_CLOSED'
  | 'RESIDENT_MOVED'
  | 'NOT_PLACED'
  | 'RECORD_MERGED'
  | 'RESIDENT_DECEASED'
  | 'COVERAGE_ACCESS_OFF'
  | 'LINK_NOT_ON_RECORD'
  | 'NO_RECORD';

/**
 * The sentence for a withheld chart, per lock code. Each names a fact about the
 * viewer's own coverage, the building, or the row's own status -- the payload
 * carries nothing more. An unknown or missing code (an older server) falls back
 * to the general statement rather than guessing a reason.
 */
export const CHART_LOCK_WORDS: Record<ChartLock, string> = {
  NO_COVERAGE_HERE: 'You do not cover this building, and no care assignment names this resident.',
  BUILDING_CLOSED: 'This building is closed for providers, so coverage does not open charts here.',
  RESIDENT_MOVED: 'This resident is no longer confirmed at this building.',
  NOT_PLACED: 'This resident has no confirmed building, so coverage cannot reach them.',
  RECORD_MERGED: 'This record was merged into another resident record. Open the kept record instead.',
  RESIDENT_DECEASED: 'This resident is recorded Deceased; coverage does not open the chart. A care assignment still can.',
  COVERAGE_ACCESS_OFF: 'Chart access from building coverage is turned off. A care assignment is required.',
  LINK_NOT_ON_RECORD: 'You cover this building, but your login has not yet been confirmed as linked to your provider record. Ask the office to confirm the link.',
  NO_RECORD: 'No resident record was found.',
};

export function chartLockWords(lock: ChartLock | null | undefined): string {
  return (lock && CHART_LOCK_WORDS[lock]) || 'Clinical information is withheld: no care assignment or building coverage.';
}

/** P1-D (2026-09-21): the visit plan Scheduling keeps in eMerge. Null fields mean no plan yet, not a missed visit. */
export interface VisitPlan {
  source: 'EMERGE';
  next: { id: string; planned_on: string; state: 'PLANNED' | 'CONFIRMED'; purpose: string | null; provider_name: string | null } | null;
  last: { id: string; completed_on: string | null; provider_name: string | null } | null;
}

/**
 * The building's manifest link (MAN-4, 2026-10-05). `current` is the one eMerge
 * holds and the one to open; `mondayReference` is Monday's column, shown only
 * as a labelled reference. A Monday sync never changes `current`.
 */
export interface DaySheetManifestLink {
  current: { url: string; source: 'EMERGE'; origin: 'EMERGE' | 'MONDAY_IMPORT'; set_at: string | null; version: number } | null;
  state: 'EMERGE' | 'CLEARED' | 'MONDAY_REFERENCE_ONLY' | 'NONE';
  mondayReference: { url: string; source: 'MONDAY'; reference: true } | null;
}

/**
 * The link a provider should open, and whether it is only Monday's reference.
 * eMerge's link first; a building eMerge holds as "no link" has none, whatever
 * Monday says. A payload from before the deploy (no `manifestLink`) falls back
 * to the scheduling row's column, which is Monday's, so it is labelled so.
 */
export function manifestLinkOf(sheet: Pick<DaySheet, 'manifestLink' | 'scheduling'>): { url: string; reference: boolean } | null {
  const ml = sheet.manifestLink;
  if (ml) {
    if (ml.current && ml.current.url) return { url: ml.current.url, reference: false };
    if (ml.state === 'CLEARED') return null;
    return ml.mondayReference ? { url: ml.mondayReference.url, reference: true } : null;
  }
  const monday = (sheet.scheduling || []).find((s) => s.cloud_manifest_url)?.cloud_manifest_url || null;
  return monday ? { url: monday, reference: true } : null;
}

export interface DaySheet {
  facility: DaySheetFacility;
  scheduling: SchedulingRow[];
  /** Optional so a payload from before the deploy still renders. */
  manifestLink?: DaySheetManifestLink | null;
  /** Optional so a payload from before the deploy still renders. */
  visitPlan?: VisitPlan | null;
  /** Devices genuinely waiting: undelivered on the board and not recorded dispensed by CounselEAR or the provider. */
  fits: PendingFit[];
  /** The same devices, one entry per resident, with the derived disposition. Optional so a payload from before the deploy still renders. */
  fitResidents?: FitResident[];
  dispensedElsewhere?: DispensedElsewhere[];
  blockedUmed: BlockedUmed[];
  contacts: { ssd: FacilityContact[]; all: FacilityContact[] };
  checkIn: { today: CheckInRecord[] };
  roster: {
    /** The selected lens's count for the whole building. */
    total: number;
    limit: number;
    offset: number;
    returned: number;
    lens: RosterLens;
    order: RosterOrder;
    /** Every lens's count, from the same predicates that selected the rows. */
    lenses: Record<RosterLens, number>;
    /** Everyone with a confirmed placement here, whatever their status. */
    confirmed: number;
    rows: RosterPatient[];
  };
  counts: {
    pendingFits: number;
    pendingFitResidents?: number;
    /** Undelivered on the board, recorded dispensed by an authoritative source. Said, never averaged. */
    dispensedElsewhere?: number;
    blockedUmed: number;
    careAssignedOnPage: number;
    /** Of those, released by building coverage alone (ruling 2026-10-20). Absent on an older payload. */
    coverageReleasedOnPage?: number;
    annualDueOnPage: number;
    offManifestOnPage?: number;
  };
  provenance: {
    rowAuthority: string;
    chartLinkPolicy: string;
    /** The caller may edit at least one manifest column here (clinical edit rights + eMerge-owned column). */
    writable: boolean;
    /** Exactly the columns the server would accept on a manifest write; absent on a payload from before the deploy. */
    writableFields?: ManifestField[];
    /** On which footing the room is offered here (rulings 2026-10-22); `room_editable` on each row decides the box. */
    roomEdit?: { provider: boolean; caseHolder: boolean; office: boolean };
    writeBlockedBy: string;
    /**
     * The status box (20261022d): offered to a provider at this building (on their own rows) or to the office (every
     * row). `settable` and `reasons` are the server's words. Absent before the deploy, which offers nothing.
     */
    statusEdit?: {
      allowed: boolean;
      as: 'PROVIDER' | 'OFFICE' | null;
      settable: string[];
      reasons: StatusReason[];
      noteMax: number;
      deathReport: boolean;
    };
    /**
     * Whether this caller may ask for a hearing-aid replacement here, and for
     * whom: SUMMARY_TIER is any resident of the building, CARE_ASSIGNED only
     * residents the caller is care-assigned to. Absent on a payload from before
     * the deploy, which offers nothing.
     */
    replacementRequest?: { allowed: boolean; gate: ReplacementGate };
    /**
     * TWL Q (ruling 2026-10-21): whether this caller may mark it here, and the
     * PA a TWL Q lead goes to, said BEFORE the click (a staff name; null when
     * nobody would be named). Absent on a payload from before the deploy, which
     * offers nothing.
     */
    twlQ?: TwlQPreview;
    /** The provider-tag vocabulary for the chips (server-owned). Absent before the deploy. */
    providerTags?: ProviderTagVocabulary;
    /**
     * "Can't find a resident?" (2026-10-28, docs/provider/CE_PULL.md): offered here when PROVIDER_CE_PULL is on and the
     * caller covers this open building with clinical edit rights. Absent or not allowed: nothing is shown.
     */
    cePull?: { allowed: boolean; by_name: boolean; add_open: boolean };
  };
}

export type TwlQRouting = 'FACILITY_PA' | 'NO_FACILITY_PA' | 'COVERAGE_NOT_CONFIRMED' | 'PA_NOT_ACTIVE' | 'PA_AMBIGUOUS';
/** Today's recorded outcome for one resident (from today's review line, not from the tags). */
export interface TwlQToday {
  outcome: ProviderTagOutcome['key'];
  disposition: string;
  /** Today's line became a PA lead. */
  lead: boolean;
}
export interface TwlQPreview {
  allowed: boolean;
  routing: TwlQRouting | null;
  pa: { name: string } | null;
  words: string | null;
  /** Residents on this page with an outcome recorded today, by resident id. Absent: nothing known. */
  today?: Record<string, TwlQToday>;
}
export interface ProviderTagOutcome {
  key: 'TWL_Q' | 'TWL_PRIVATE_PAY' | 'TWL_MEDICAID_PENDING' | 'TWL_DNQ' | 'TWL_HOSPICE';
  /** The tally word sent to the server, exactly as the business writes it. */
  word: string;
  label: string;
  /** The provider-tag words this outcome adds. */
  tags: string[];
  sends_to_pa: boolean;
}
export interface ProviderTagVocabulary {
  groups: { key: string; label: string; words: string[] }[];
  outcomes: ProviderTagOutcome[];
  /** Words that arrive only through an outcome (TWL, QUALIFIED). */
  outcome_tags: string[];
}
export type TwlQResultCode =
  | 'ASSIGNED' | 'HELD_ON_DESK' | 'UNASSIGNED' | 'ALREADY_A_LEAD' | 'RESIDENT_HAS_ANOTHER_PA' | 'NOT_A_NEW_LEAD' | 'RECORDED';
export interface TwlQResult {
  result: TwlQResultCode;
  outcome: ProviderTagOutcome['key'];
  word: string;
  label: string;
  sent_to_pa: boolean;
  pa: { name: string } | null;
  routing: TwlQRouting | null;
  readiness: 'READY' | 'HELD' | null;
  held_by: { code: string; what: string }[];
  replayed: boolean;
  /** TWL · Medicaid pending only: the building's PA has a notice waiting. Not a lead. */
  pa_told?: boolean;
  visit_date: string;
  /** The answer in words, for the screen. */
  message: string;
  /** The provider tags as stored after the mark; null when nothing was written. */
  provider_tags: string[] | null;
  code?: string;
}

export type ReplacementGate = 'SUMMARY_TIER' | 'CARE_ASSIGNED';
export type ReplacementSide = 'LEFT' | 'RIGHT' | 'BOTH';
export type ReplacementReason = 'LOST' | 'DAMAGED' | 'NOT_WORKING' | 'RECHARGEABLE_UPGRADE' | 'WARRANTY_REPLACEMENT' | 'OTHER';

/** The reasons a provider can see in the building. How it is paid for is the office's call, so it is not asked. */
export const FIELD_REPLACEMENT_REASONS: { value: ReplacementReason; label: string }[] = [
  { value: 'LOST', label: 'Lost' },
  { value: 'DAMAGED', label: 'Damaged' },
  { value: 'NOT_WORKING', label: 'Not working' },
  { value: 'OTHER', label: 'Other (say what in the note)' },
];

export interface ReplacementRequestInput {
  side: ReplacementSide;
  reason_code: ReplacementReason;
  note: string | null;
  /** One per attempt; a retry with the same key is the same request. */
  idempotency_key: string;
}

export interface ReplacementRequestResult {
  id: string;
  state: 'OPEN' | 'PLACED' | 'CANCELLED';
  replayed: boolean;
  requested_at: string | null;
}

/** May this caller ask for a replacement for this resident? The server's word; nothing offered without it. */
export function mayRequestReplacement(sheet: Pick<DaySheet, 'provenance'>, patient: Pick<RosterPatient, 'care_assigned'>): boolean {
  const rr = sheet.provenance.replacementRequest;
  if (!rr || !rr.allowed) return false;
  return rr.gate === 'SUMMARY_TIER' || (rr.gate === 'CARE_ASSIGNED' && patient.care_assigned === true);
}

/** The manifest columns a provider may own. Which of them are open is the server's word (`provenance.writableFields`). */
export type ManifestField = 'tags' | 'provider_tags' | 'alerts' | 'notes' | 'qualification_status' | 'room' | 'status';

/** Why a resident is Inactive in eMerge (ruling 2026-10-22: required with Inactive). */
export type StatusReason = 'DISCHARGED' | 'MOVED' | 'OTHER';

/** The answer to a status save (PUT .../status, 20261022d): the status now shown, and whether it is eMerge's. */
export interface StatusSaveResult {
  id: string;
  changed: boolean;
  change_id: string | null;
  status: string | null;
  status_edited: boolean;
  status_monday: string | null;
  status_reason: StatusReason | null;
  status_note: string | null;
  status_edited_at: string | null;
}

/** What a status save sends. `status: null` goes back to Monday's; `expected_status` is the status the screen showed. */
export interface StatusSaveInput {
  status: 'Active' | 'Inactive' | null;
  reason?: StatusReason;
  note?: string;
  expected_status: string | null;
}

/** An open death-report request on a roster row. */
export interface DeathRequest {
  id: string;
  effective_date: string | null;
  mine: boolean;
  withdrawable: boolean;
}

/** The answer to a death report (POST .../deceased-report): a REQUEST Finance confirms, never a status. */
export interface DeathReportResult {
  id: string;
  requested: boolean;
  replayed: boolean;
  request_id: string;
  message: string;
}

/** The answer to a room save (PUT .../room, 20261022a): the room now shown, and whether it is the provider's. */
export interface RoomSaveResult {
  id: string;
  changed: boolean;
  change_id: string | null;
  room_number: string | null;
  room_edited: boolean;
  room_monday: string | null;
  room_edited_at: string | null;
}
export type ManifestPatch = Partial<{ notes: string | null; alerts: string | null; provider_tags: string[] | null; tags: string[] | null }>;

/**
 * The caller's buildings' schedule (ruling 2026-10-19): the eMerge visit plans
 * the Scheduling board reads, for the buildings in the caller's own scope —
 * which, for a provider, are the buildings their coverage granted. Mirrors
 * `GET /emerge/day-sheet/schedule` (services/scheduling/providerSchedule.js).
 * Dates are calendar days (YYYY-MM-DD). No resident, no plan notes.
 */
export interface ScheduledVisit {
  id: string;
  facility: { id: string; name: string | null; city: string | null };
  state: 'PLANNED' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED';
  planned_on: string | null;
  completed_on: string | null;
  purpose: string | null;
  provider_name: string | null;
  /** The plan names the caller's own provider record. Display only. */
  is_yours: boolean;
  /** Open plans of the selected building only: the date has passed and Scheduling has not closed it. */
  passed?: boolean;
  /** The plan's building is closed for scheduling. */
  building_closed?: BuildingClosure | null;
}
export interface ScheduleFacility {
  id: string;
  name: string | null;
  city: string | null;
  state: string | null;
  next_plan: { id: string; planned_on: string; state: 'PLANNED' | 'CONFIRMED'; purpose: string | null; provider_name: string | null } | null;
  last_completed: { id: string; completed_on: string | null; provider_name: string | null } | null;
  closure?: BuildingClosure | null;
}
export interface ProviderSchedule {
  day: string;
  source: 'EMERGE';
  /** An enterprise caller is handed no estate-wide list: their explicit buildings only, plus one they name. */
  enterprise: boolean;
  facilities: ScheduleFacility[];
  upcoming: { items: ScheduledVisit[]; total: number; limit: number };
  completed: { items: ScheduledVisit[]; total: number; limit: number; window: { from: string; through: string; days: number } };
  selected: { facility: { id: string; name: string | null; city: string | null; state: string | null; closure?: BuildingClosure | null }; open: ScheduledVisit[]; completed: ScheduledVisit[] } | null;
}

/**
 * The provider's own planned visits for one calendar day (`GET /emerge/day-sheet/my-day`).
 * Visits arrive in route order (nulls last), then arrival time, then building name; the client never re-sorts.
 * `outside_scope` is a count only. `linked: false` means the login has no provider record yet.
 */
export interface MyDayVisit {
  plan_id: string;
  date: string;
  state: 'PLANNED' | 'CONFIRMED' | 'COMPLETED';
  purpose: string | null;
  /** The plan's notes written by Scheduling when planning. Plain text. */
  instructions: string | null;
  facility: {
    id: string;
    name: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zip_code: string | null;
    address_line: string | null;
    maps: { query: string; from_address: boolean; apple: string; google: string } | null;
  };
  building_closed: BuildingClosure | null;
  /** Planned arrival, 'HH:MM' 24-hour America/New_York local time; null when Scheduling set none. */
  arrival_time?: string | null;
  /** Stop number on the day's route, 1-20; null when Scheduling set none. */
  route_order?: number | null;
  /** Today's round at this building (FR-2): counts only, present for today's visits only. */
  round?: RoundProgress | null;
}
export interface MyDayDay { date: string; visits: number; facilities: number; closed: number }
export interface MyDay {
  linked: boolean;
  provider: { name: string | null } | null;
  /** Present when `linked` is false. */
  reason?: string;
  message?: string;
  enterprise?: boolean;
  today?: string;
  window?: { from: string; through: string; days: number };
  order?: 'ROUTE_ORDER_THEN_TIME_THEN_NAME';
  days: MyDayDay[];
  date: string | null;
  selected: { date: string; visits: MyDayVisit[]; outside_scope: number } | null;
  outside_scope: number;
  focus?: string | null;
}

interface ApiResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  /** Present on a refusal: the authorization decision that produced it. */
  code?: string;
  message?: string;
  /** The HTTP status; absent with no code means the server was never reached (services/api contract). */
  status?: number;
}

/**
 * `api.get` is `apiRequest` bound to a verb, and binding loses the generic —
 * so every call casts its own result. Same treatment as `emergeApi.ts`.
 */
export const providerApi = {
  /**
   * Which buildings this provider may open. `q` searches, and only an
   * enterprise-scoped caller is offered it — a provider with six buildings
   * does not need to search six buildings.
   */
  getScope: async (q?: string): Promise<ApiResult<FacilityScope>> =>
    (await api.get(`/emerge/day-sheet${q ? `?q=${encodeURIComponent(q)}` : ''}`)) as ApiResult<FacilityScope>,

  /**
   * The schedule across the caller's buildings, and one building's plan when
   * named. A building outside the caller's scope is 404; no scope at all is 403
   * DENY_NO_CAPABILITY — both said on screen, never an empty schedule.
   */
  getSchedule: async (facilityId?: string | null): Promise<ApiResult<ProviderSchedule>> =>
    (await api.get(`/emerge/day-sheet/schedule${facilityId ? `?facility_id=${encodeURIComponent(facilityId)}` : ''}`)) as ApiResult<ProviderSchedule>,

  /**
   * The caller's own planned visits for one day (default: the server picks today or the next visit day).
   * 403 DENY_NO_CAPABILITY is no building assigned yet; 404 is a facility outside scope; 400 DATE_INVALID.
   */
  getMyDay: async (date?: string | null, facilityId?: string | null): Promise<ApiResult<MyDay>> => {
    const qp = new URLSearchParams();
    if (date) qp.set('date', date);
    if (facilityId) qp.set('facility_id', facilityId);
    const qs = qp.toString();
    return (await api.get(`/emerge/day-sheet/my-day${qs ? `?${qs}` : ''}`)) as ApiResult<MyDay>;
  },

  /** Record that the caller has arrived, with whom. An event, not a checkbox. */
  checkIn: async (facilityId: string, contactId: string | null): Promise<ApiResult<CheckInRecord>> =>
    (await api.post(`/emerge/day-sheet/${facilityId}/check-in`, { contact_id: contactId })) as ApiResult<CheckInRecord>,

  /**
   * Change the manifest columns the provider owns for one care-assigned
   * resident (workforce cutover, 2026-09-19). Only the fields the server named
   * in `provenance.writableFields` are sent; the server refuses the rest.
   */
  updateManifest: async (facilityId: string, patientId: string, patch: ManifestPatch): Promise<ApiResult<{ id: string; updated: string[] }>> =>
    (await api.patch(`/emerge/day-sheet/${facilityId}/patients/${patientId}`, patch)) as ApiResult<{ id: string; updated: string[] }>,

  /**
   * Correct a resident's room (20261022a): offered only when `provenance.writableFields` names `room` and the
   * resident is care-assigned. An empty room clears the correction and Monday's room shows again. Monday's own
   * room_number is never written. Refusals carry a plain `message`.
   */
  setRoom: async (facilityId: string, patientId: string, room: string): Promise<ApiResult<RoomSaveResult>> =>
    (await api.put(`/emerge/day-sheet/${facilityId}/patients/${patientId}/room`, { room })) as ApiResult<RoomSaveResult>,

  /**
   * Set a resident's status in eMerge (20261022d): Active, Inactive (with a reason, and an optional note), or null to
   * go back to Monday's. Monday's own status is never written. 409 STATUS_STALE when the status changed since the
   * page loaded. Refusals carry a plain `message`.
   */
  setStatus: async (facilityId: string, patientId: string, input: StatusSaveInput): Promise<ApiResult<StatusSaveResult>> =>
    (await api.put(`/emerge/day-sheet/${facilityId}/patients/${patientId}/status`, input)) as ApiResult<StatusSaveResult>,

  /**
   * Ask Finance to record a resident's death (ruling 2026-10-22): a REQUEST, never a status, and nothing else changes
   * until Finance confirms it. Asking again while it is open answers with the same request.
   */
  reportDeath: async (facilityId: string, patientId: string, effectiveDate: string): Promise<ApiResult<DeathReportResult>> =>
    (await api.post(`/emerge/day-sheet/${facilityId}/patients/${patientId}/deceased-report`, { effective_date: effectiveDate })) as ApiResult<DeathReportResult>,

  /** Withdraw an open death-report request (the provider who filed it, or the office). */
  withdrawDeathReport: async (facilityId: string, patientId: string, requestId: string): Promise<ApiResult<{ withdrawn: boolean; request_id: string }>> =>
    (await api.post(`/emerge/day-sheet/${facilityId}/patients/${patientId}/deceased-report/withdraw`, { request_id: requestId })) as ApiResult<{ withdrawn: boolean; request_id: string }>,

  /**
   * Ask for a hearing-aid replacement for one resident (2026-10-20). Lands on
   * the office's Replacements desk; the PA managers and the building's PA are
   * told. Refusals carry a plain `message` (REQUEST_ALREADY_OPEN,
   * PATIENT_NOT_AT_FACILITY, FACILITY_DISAGREES, ...). 201 new, 200 replayed.
   */
  requestReplacement: async (facilityId: string, patientId: string, input: ReplacementRequestInput): Promise<ApiResult<ReplacementRequestResult>> =>
    (await api.post(`/emerge/day-sheet/${facilityId}/patients/${patientId}/replacement-request`, input)) as ApiResult<ReplacementRequestResult>,

  /**
   * Mark a resident's TWL Q outcome (ruling 2026-10-21). TWL Q goes to the
   * facility's PA; the other four are recorded on the day's manifest review.
   * The key is one per attempt and is kept across a lost answer, so a retry
   * cannot send twice. Refusals carry a plain `message`.
   */
  markTwlQ: async (facilityId: string, patientId: string, input: { outcome: string; idempotency_key: string }): Promise<ApiResult<TwlQResult>> =>
    (await api.post(`/emerge/day-sheet/${facilityId}/patients/${patientId}/twl-q`, input)) as ApiResult<TwlQResult>,

  getDaySheet: async (
    facilityId: string,
    params: { limit?: number; offset?: number; lens?: RosterLens | null; order?: RosterOrder } = {}
  ): Promise<ApiResult<DaySheet>> => {
    const qp = new URLSearchParams();
    if (params.lens && params.lens !== DEFAULT_LENS) qp.set('lens', params.lens);
    if (params.order && params.order !== 'name') qp.set('order', params.order);
    if (params.limit) qp.set('limit', String(params.limit));
    if (params.offset) qp.set('offset', String(params.offset));
    const qs = qp.toString();
    return (await api.get(
      `/emerge/day-sheet/${facilityId}${qs ? `?${qs}` : ''}`
    )) as ApiResult<DaySheet>;
  },
};

/** "Closed for scheduling (dropped)" -- the Kanban's words for a closed building. */
export function closureWords(c: BuildingClosure | null | undefined): string | null {
  if (!c) return null;
  if (c.source === 'BUSINESS_STATUS') return 'Dropped (business status)';
  if (c.kind === 'MERGED') return c.into ? `Merged into ${c.into}` : 'Merged into another building';
  const kind = c.kind === 'DO_NOT_SCHEDULE' ? 'do not schedule' : c.kind === 'DROPPED' ? 'dropped' : 'closed';
  return `Closed for scheduling (${kind})`;
}

/**
 * The accurate sentence for a closure, per cause (third verification, 2026-10-19; backend buildingClosure.js
 * closureSentence): a business status of Dropped is not "closed for scheduling". No full stop.
 */
export function closureSentence(c: BuildingClosure | null | undefined): string | null {
  if (!c) return null;
  if (c.source === 'BUSINESS_STATUS') return 'This building is marked Dropped';
  if (c.source === 'MERGED' || c.kind === 'MERGED') return `This building was merged into ${c.into || 'another building'}`;
  const kind = c.kind === 'DO_NOT_SCHEDULE' ? 'do not schedule' : c.kind === 'DROPPED' ? 'dropped' : 'closed';
  return `This building is closed for scheduling (${kind})`;
}

/** A patient's display name, without inventing one when both parts are absent. */

export function patientName(p: {
  first_name: string | null;
  last_name: string | null;
}): string {
  const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
  return name || 'Name not recorded';
}

/**
 * Every date this portal shows is a Monday calendar day.
 *
 * The scheduling board stores them as `date` columns (five of them) or as a
 * timestamptz at midnight UTC (`provider_next_visit_at`, all 258 rows), and
 * the API hands them over as "2026-09-08T00:00:00.000Z". Read as a local
 * instant in Eastern time that is 8 PM on September 7 — which is how the
 * Schedule tab showed a September 8 visit a day early, the same defect the
 * coverage board shipped with (#87) and Today fixed for itself (#94).
 *
 * So the calendar day is the UTC date, taken here once for every caller.
 * A date-only string ("2026-09-08") parses as UTC midnight too, so it gets
 * the same answer. Verified 2026-09-03 against every column that reaches
 * these helpers: no caller carries a true time of day.
 */
function calendarDay(value: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return { y: parsed.getUTCFullYear(), m: parsed.getUTCMonth(), d: parsed.getUTCDate() };
}

/**
 * A null renders as an em dash rather than as today's date, which is what
 * `new Date(null)` would quietly produce.
 */
export function shortDate(value: string | null | undefined): string {
  const c = calendarDay(value);
  if (!c) return '—';
  return new Date(Date.UTC(c.y, c.m, c.d)).toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
  });
}

/**
 * Whole days from the reader's own calendar day; negative is in the past.
 * Null when there is no date.
 */
export function daysFromToday(value: string | null | undefined): number | null {
  const c = calendarDay(value);
  if (!c) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const that = new Date(c.y, c.m, c.d).getTime();
  return Math.round((that - today) / 86400000);
}

/** "in 3 days" / "yesterday" / "today" — relative, because that is the decision. */
export function relativeDay(value: string | null | undefined): string | null {
  const days = daysFromToday(value);
  if (days === null) return null;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}
